import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  setDoc,
  Timestamp
} from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faCircleExclamation,
  faClipboardList,
  faWallet
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { db } from '../../firebase';
import '../../Pages/dash.css';
import './marketingExpenses.css';
import {
  formatKes,
  formatShortDate,
  getDateInputValue,
  getMonthKey,
  normalizeMarketingExpenseStatus,
  normalizeAmount,
  summarizeMarketingExpenses,
  toDateValue
} from '../../utils/financeWorkflow';

const EXPENSE_STATUS_OPTIONS = ['Pending', 'Paid'];
const CARD_STATUS_OPTIONS = ['All', 'Not started', 'Pending payment', 'Partially paid', 'Settled', 'Amount not set'];

const buildEmptyForm = (defaultBudgetId = '', defaults = {}) => ({
  budgetId: defaultBudgetId,
  expenseTitle: '',
  expenseDate: getDateInputValue(),
  expenseAmount: '',
  paymentStatus: 'Paid',
  notes: '',
  ...defaults
});

const getStatusTone = (expense) => {
  const normalized = normalizeMarketingExpenseStatus(expense);

  if (normalized === 'paid' || normalized === 'processed' || normalized === 'completed') {
    return 'success';
  }

  if (normalized === 'cancelled' || normalized === 'canceled') {
    return 'danger';
  }

  if (normalized === 'pending') {
    return 'warning';
  }

  return 'secondary';
};

function MarketingExpensesComp() {
  const navigate = useNavigate();
  const auth = getAuth();
  const [budgets, setBudgets] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [paymentListShow, setPaymentListShow] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editID, setEditID] = useState(null);
  const [activeBudgetCard, setActiveBudgetCard] = useState(null);
  const [budgetFilter, setBudgetFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [formState, setFormState] = useState(buildEmptyForm());
  const currentMonthKey = useMemo(() => getMonthKey(new Date()), []);

  const fetchPage = useCallback(async () => {
    setLoading(true);

    try {
      const [budgetSnapshot, expenseSnapshot] = await Promise.all([
        getDocs(collection(db, 'marketingBudget')),
        getDocs(collection(db, 'marketingExpenses'))
      ]);

      const nextBudgets = budgetSnapshot.docs.map((item) => {
        const data = item.data();
        return {
          id: item.id,
          ...data,
          docID: data.docID || item.id
        };
      }).sort((first, second) => {
        const firstTime = toDateValue(first.dateAdded)?.getTime() || 0;
        const secondTime = toDateValue(second.dateAdded)?.getTime() || 0;
        return secondTime - firstTime;
      });

      const nextExpenses = expenseSnapshot.docs.map((item) => {
        const data = item.data();
        return {
          id: item.id,
          ...data,
          docID: data.docID || item.id
        };
      });

      setBudgets(nextBudgets);
      setExpenses(nextExpenses);
    } catch (error) {
      console.error('Failed to load marketing expenses:', error);
      swal('Error', 'Marketing payments could not be loaded right now.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const budgetsById = useMemo(() => {
    return budgets.reduce((accumulator, budget) => {
      accumulator[String(budget.docID || budget.id || '').trim()] = budget;
      return accumulator;
    }, {});
  }, [budgets]);

  const budgetSummaries = useMemo(() => {
    return summarizeMarketingExpenses({
      budgets,
      expenses
    });
  }, [budgets, expenses]);

  const overview = useMemo(() => {
    return budgetSummaries.reduce(
      (accumulator, budget) => {
        accumulator.totalBudget += budget.budgetAmount;
        accumulator.totalPaid += budget.paidAmount;
        accumulator.totalPending += budget.pendingAmount;
        accumulator.totalRemaining += budget.remainingAmount;
        accumulator.totalExpenses += budget.expenseCount;

        if (budget.expenseCount > 0) {
          accumulator.activeBudgets += 1;
        }

        if (budget.remainingAmount <= 0 && budget.budgetAmount > 0) {
          accumulator.settledBudgets += 1;
        }

        return accumulator;
      },
      {
        totalBudget: 0,
        totalPaid: 0,
        totalPending: 0,
        totalRemaining: 0,
        totalExpenses: 0,
        activeBudgets: 0,
        settledBudgets: 0
      }
    );
  }, [budgetSummaries]);

  const expenseLedger = useMemo(() => {
    return [...expenses].sort((first, second) => {
      const firstTime = toDateValue(first.expenseDate || first.createdAt || first.updatedAt)?.getTime() || 0;
      const secondTime = toDateValue(second.expenseDate || second.createdAt || second.updatedAt)?.getTime() || 0;
      return secondTime - firstTime;
    }).map((expense) => {
      const budgetId = String(
        expense.budgetId
        || expense.marketingBudgetId
        || expense.budget
        || expense.budgetDocId
        || ''
      ).trim();
      const linkedBudget = budgetsById[budgetId];

      return {
        id: expense.id || expense.docID,
        budgetId,
        budgetMedium: linkedBudget?.medium || expense.budgetMedium || 'Budget removed',
        expenseTitle: expense.expenseTitle || expense.title || 'Marketing expense',
        expenseAmount: Number(expense.expenseAmount || expense.amount || 0),
        expenseDate: expense.expenseDate || expense.createdAt || expense.updatedAt,
        paymentStatus: expense.paymentStatus || expense.status || 'Pending',
        notes: expense.notes || '',
        paidAt: expense.paidAt || null,
        createdAt: expense.createdAt || null,
        updatedAt: expense.updatedAt || null
      };
    });
  }, [budgetsById, expenses]);

  const budgetCards = useMemo(() => {
    return budgetSummaries.map((budget) => {
      const budgetId = String(budget.docID || budget.id || '').trim();
      const paymentHistory = [...expenseLedger]
        .filter((expense) => expense.budgetId === budgetId)
        .sort((first, second) => {
          const firstTime = toDateValue(first.expenseDate || first.createdAt || first.updatedAt)?.getTime() || 0;
          const secondTime = toDateValue(second.expenseDate || second.createdAt || second.updatedAt)?.getTime() || 0;
          return secondTime - firstTime;
        });

      const paidThisMonth = paymentHistory
        .filter((expense) => {
          if (normalizeMarketingExpenseStatus(expense) !== 'paid') {
            return false;
          }

          return getMonthKey(expense.expenseDate || expense.createdAt || expense.updatedAt) === currentMonthKey;
        })
        .reduce((sum, expense) => sum + normalizeAmount(expense.expenseAmount || expense.amount), 0);

      const paidThisMonthCount = paymentHistory.filter((expense) => (
        normalizeMarketingExpenseStatus(expense) === 'paid'
        && getMonthKey(expense.expenseDate || expense.createdAt || expense.updatedAt) === currentMonthKey
      )).length;

      const recentNotes = paymentHistory
        .map((expense) => [expense.expenseTitle, expense.notes, expense.paymentStatus].filter(Boolean).join(' '))
        .join(' ');

      return {
        ...budget,
        budgetId,
        paymentHistory,
        paidThisMonth,
        paidThisMonthCount,
        recentNotes,
        searchBlob: [
          budget.medium,
          budget.paymentStatus,
          budget.paymentTone,
          formatKes(budget.budgetAmount),
          formatKes(budget.paidAmount),
          formatKes(budget.remainingAmount),
          formatKes(paidThisMonth),
          recentNotes
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
      };
    });
  }, [budgetSummaries, currentMonthKey, expenseLedger]);

  const filteredBudgetCards = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return budgetCards.filter((budget) => {
      const matchesBudget = budgetFilter === 'All' || budget.budgetId === budgetFilter;
      const matchesStatus = statusFilter === 'All' || budget.paymentStatus === statusFilter;

      if (!matchesBudget || !matchesStatus) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      return budget.searchBlob.includes(normalizedSearch);
    });
  }, [budgetCards, budgetFilter, searchTerm, statusFilter]);

  const selectedBudget = useMemo(() => {
    return budgetsById[formState.budgetId] || null;
  }, [budgetsById, formState.budgetId]);

  const openCreateModal = (budgetId = '') => {
    if (budgets.length === 0) {
      swal(
        'Create the budget first',
        'Please add a marketing budget before recording marketing payments so each entry can be tracked against a budget line.',
        'warning'
      );
      navigate('/marketing-budget');
      return;
    }

    const defaultBudgetId = budgetId || budgets[0]?.docID || budgets[0]?.id || '';
    const budget = budgetsById[defaultBudgetId] || budgets[0] || null;

    setEditing(false);
    setEditID(null);
    setFormState({
      ...buildEmptyForm(defaultBudgetId, {
        expenseTitle: budget?.medium ? `${budget.medium} payment` : '',
        paymentStatus: 'Paid'
      })
    });
    setModalShow(true);
  };

  const openPaymentListModal = (budgetCard) => {
    setActiveBudgetCard(budgetCard);
    setPaymentListShow(true);
  };

  const closePaymentListModal = () => {
    setPaymentListShow(false);
    setActiveBudgetCard(null);
  };

  const openEditModal = (expense) => {
    setEditing(true);
    setEditID(expense.id);
    setFormState({
      budgetId: expense.budgetId,
      expenseTitle: expense.expenseTitle,
      expenseDate: getDateInputValue(expense.expenseDate || expense.createdAt || new Date()),
      expenseAmount: String(expense.expenseAmount || ''),
      paymentStatus: String(expense.paymentStatus || expense.status || 'Pending'),
      notes: expense.notes || ''
    });
    setModalShow(true);
  };

  const closeModal = () => {
    if (submitting) {
      return;
    }

    setModalShow(false);
  };

  const resetForm = () => {
    setEditing(false);
    setEditID(null);
    setFormState(buildEmptyForm(budgets[0]?.docID || budgets[0]?.id || ''));
    setModalShow(false);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    if (name === 'budgetId') {
      const nextBudget = budgetsById[value];
      setFormState((currentForm) => ({
        ...currentForm,
        budgetId: value,
        expenseTitle: currentForm.expenseTitle || (nextBudget?.medium ? `${nextBudget.medium} payment` : '')
      }));
      return;
    }

    setFormState((currentForm) => ({
      ...currentForm,
      [name]: value
    }));
  };

  const refreshAfterWrite = async () => {
    await fetchPage();
    setModalShow(false);
    setEditing(false);
    setEditID(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!auth.currentUser) {
      swal('Error', 'You need to be signed in to record a marketing payment.', 'error');
      return;
    }

    if (!formState.budgetId || !budgetsById[formState.budgetId]) {
      swal('Missing budget', 'Please choose a marketing budget first.', 'warning');
      return;
    }

    const amountValue = Number(formState.expenseAmount);
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      swal('Invalid amount', 'Payment amount should be greater than zero.', 'warning');
      return;
    }

    const expenseDateValue = new Date(formState.expenseDate);
    if (Number.isNaN(expenseDateValue.getTime())) {
      swal('Invalid date', 'Please choose a valid payment date.', 'warning');
      return;
    }

    const paymentStatus = formState.paymentStatus === 'Paid' ? 'Paid' : 'Pending';
    const budget = budgetsById[formState.budgetId];
    const id = editing ? editID : `me_${Date.now()}`;
    const existingExpense = expenses.find((item) => (item.id || item.docID) === id);

    setSubmitting(true);

    try {
      await setDoc(doc(db, 'marketingExpenses', id), {
        budgetId: formState.budgetId,
        budgetMedium: budget?.medium || 'Budget removed',
        expenseTitle: formState.expenseTitle.trim() || `${budget?.medium || 'Marketing'} payment`,
        expenseAmount: amountValue,
        expenseDate: Timestamp.fromDate(expenseDateValue),
        paymentStatus,
        status: paymentStatus,
        notes: formState.notes.trim(),
        createdAt: existingExpense?.createdAt || Timestamp.now(),
        updatedAt: Timestamp.now(),
        paidAt: paymentStatus === 'Paid' ? (existingExpense?.paidAt || Timestamp.now()) : null,
        createdBy: editing ? (existingExpense?.createdBy || auth.currentUser.uid) : auth.currentUser.uid,
        docID: id
      });

      swal('Success', `Marketing payment ${editing ? 'updated' : 'recorded'} successfully.`, 'success');
      await refreshAfterWrite();
    } catch (error) {
      console.error('Failed to save marketing expense:', error);
      swal('Error', 'Marketing payment could not be saved right now.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteExpense = async (expense) => {
    swal({
      title: 'Delete this payment?',
      text: 'This will remove the marketing payment record from the ledger.',
      icon: 'warning',
      buttons: true,
      dangerMode: true
    }).then(async (willDelete) => {
      if (!willDelete) {
        return;
      }

      try {
        await deleteDoc(doc(db, 'marketingExpenses', expense.id));
        swal('Deleted!', 'Marketing payment has been deleted.', 'success');
        await fetchPage();
      } catch (error) {
        console.error('Failed to delete marketing expense:', error);
        swal('Error', 'Marketing payment could not be deleted right now.', 'error');
      }
    });
  };

  const openBudgetPage = () => {
    navigate('/marketing-budget');
  };

  return (
    <div className="marketingExpensesPage">
      <header className="marketingExpensesHeader">
        <div>
          <span className="marketingExpensesEyebrow">Marketing / Finance</span>
          <h1>Marketing expenses</h1>
          <p>Track budget payments and what remains on each line.</p>
        </div>
        <div className="marketingExpensesHeaderActions">
          <button type="button" className="marketingExpensesSecondaryButton" onClick={openBudgetPage}>
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> Marketing budgets
          </button>
          <button type="button" className="marketingExpensesPrimaryButton" onClick={() => openCreateModal()}>
            <FontAwesomeIcon icon={faWallet} /> Make payment
          </button>
        </div>
      </header>

      <section className="marketingExpensesOverview" aria-label="Marketing expense summary">
        <div className="marketingExpensesMetric">
          <span>Budget tracked</span>
          <strong>{formatKes(overview.totalBudget)}</strong>
          <small>{budgetSummaries.length} budget lines</small>
        </div>
        <div className="marketingExpensesMetric">
          <span>Paid this month</span>
          <strong>{formatKes(budgetCards.reduce((sum, budget) => sum + budget.paidThisMonth, 0))}</strong>
          <small>{budgetCards.reduce((sum, budget) => sum + budget.paidThisMonthCount, 0)} payments</small>
        </div>
        <div className="marketingExpensesMetric">
          <span>Remaining</span>
          <strong>{formatKes(overview.totalRemaining)}</strong>
          <small>Across all budget lines</small>
        </div>
        <div className="marketingExpensesMetric">
          <span>Recorded payments</span>
          <strong>{overview.totalExpenses}</strong>
          <small>{overview.settledBudgets} budgets settled</small>
        </div>
      </section>

      <section className="marketingExpensesRegister">
        <div className="marketingExpensesRegisterHeader">
          <div>
            <h2>Budget register</h2>
            <p>Choose a budget line to view, edit, or record its payments.</p>
          </div>
          <div className="marketingExpensesFilters">
            <Form.Control
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search budgets or payments"
              aria-label="Search budgets or payments"
            />
            <Form.Select
              value={budgetFilter}
              onChange={(event) => setBudgetFilter(event.target.value)}
              aria-label="Filter by budget"
            >
              <option value="All">All budgets</option>
              {budgets.map((budget) => (
                <option key={budget.docID || budget.id} value={budget.docID || budget.id}>
                  {budget.medium}
                </option>
              ))}
            </Form.Select>
            <Form.Select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              aria-label="Filter by status"
            >
              <option value="All">All statuses</option>
              {CARD_STATUS_OPTIONS.slice(1).map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </Form.Select>
          </div>
        </div>

        {loading ? (
          <div className="marketingExpensesState"><Spinner animation="border" role="status" /><span>Loading marketing expenses...</span></div>
        ) : filteredBudgetCards.length === 0 ? (
          <div className="marketingExpensesEmpty">
            <FontAwesomeIcon icon={faCircleExclamation} />
            <h3>{budgetCards.length === 0 ? 'No marketing budgets yet' : 'No matching budget lines'}</h3>
            <p>{budgetCards.length === 0 ? 'Create a budget first to start recording its payments.' : 'Try changing your search or filters.'}</p>
            {budgetCards.length === 0 ? (
              <button type="button" className="marketingExpensesSecondaryButton" onClick={openBudgetPage}>
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> Open marketing budgets
              </button>
            ) : null}
          </div>
        ) : (
          <div className="marketingExpensesTableWrap">
            <table className="marketingExpensesTable">
              <thead>
                <tr>
                  <th>Budget line</th>
                  <th>Budget</th>
                  <th>Paid</th>
                  <th>Remaining</th>
                  <th>Status</th>
                  <th>Paid this month</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {filteredBudgetCards.map((budget) => (
                  <tr key={budget.budgetId}>
                    <td data-label="Budget line">
                      <div className="marketingExpensesBudgetName">
                        <strong>{budget.medium}</strong>
                        <span>{budget.expenseCount} payment{budget.expenseCount === 1 ? '' : 's'}</span>
                      </div>
                    </td>
                    <td data-label="Budget">{formatKes(budget.budgetAmount)}</td>
                    <td data-label="Paid">{formatKes(budget.paidAmount)}</td>
                    <td data-label="Remaining" className="marketingExpensesRemaining">{formatKes(budget.remainingAmount)}</td>
                    <td data-label="Status">
                      <span className={`marketingExpensesStatus marketingExpensesStatus--${budget.paymentTone || 'neutral'}`}>
                        {budget.paymentStatus}
                      </span>
                    </td>
                    <td data-label="Paid this month">
                      <div className="marketingExpensesPaidMonth">
                        <strong>{formatKes(budget.paidThisMonth)}</strong>
                        <span>{budget.paidThisMonthCount} payment{budget.paidThisMonthCount === 1 ? '' : 's'}</span>
                      </div>
                    </td>
                    <td data-label="Actions" className="marketingExpensesActionsCell">
                      <button type="button" className="marketingExpensesTextButton" onClick={() => openPaymentListModal(budget)}>
                        <FontAwesomeIcon icon={faClipboardList} /> Payments
                      </button>
                      <button type="button" className="marketingExpensesTextButton marketingExpensesTextButton--primary" onClick={() => openCreateModal(budget.budgetId)}>
                        <FontAwesomeIcon icon={faWallet} /> Pay
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Modal show={paymentListShow} onHide={closePaymentListModal} centered size="lg" contentClassName="marketingExpensesModal">
        <Modal.Header closeButton>
          <Modal.Title>Payment list</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeBudgetCard ? (
            <div className="marketingExpensePaymentListModal">
              <div className="marketingExpensePaymentListHero">
                <div>
                  <span>{activeBudgetCard.paymentStatus}</span>
                  <h5>{activeBudgetCard.medium}</h5>
                  <p>
                    Track every payment tied to this budget card, including what has been paid this month and what remains.
                  </p>
                </div>
                <div className="marketingExpensePaymentListStats">
                  <div>
                    <span>Paid this month</span>
                    <strong>{formatKes(activeBudgetCard.paidThisMonth)}</strong>
                  </div>
                  <div>
                    <span>Remaining</span>
                    <strong>{formatKes(activeBudgetCard.remainingAmount)}</strong>
                  </div>
                  <div>
                    <span>Paid total</span>
                    <strong>{formatKes(activeBudgetCard.paidAmount)}</strong>
                  </div>
                </div>
              </div>

              <div className="marketingExpensePaymentListStream">
                {activeBudgetCard.paymentHistory.length > 0 ? (
                  activeBudgetCard.paymentHistory.map((expense) => (
                    <div key={expense.id} className="marketingExpensePaymentListItem">
                      <div className="marketingExpensePaymentListCopy">
                        <strong>{expense.expenseTitle}</strong>
                        <span>
                          {formatShortDate(expense.expenseDate)} · {expense.notes || 'No notes'}
                        </span>
                      </div>
                      <div className="marketingExpensePaymentListMeta">
                        <strong>{formatKes(expense.expenseAmount)}</strong>
                        <span className={`marketingExpenseStatusPill marketingExpenseStatusPill--${getStatusTone(expense)}`}>
                          {expense.paymentStatus}
                        </span>
                        <div className="marketingExpensePaymentListActions">
                          <Button
                            type="button"
                            variant="outline-dark"
                            size="sm"
                            className="marketingExpenseActionBtn"
                            onClick={() => {
                              closePaymentListModal();
                              openEditModal(expense);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            type="button"
                            variant="outline-danger"
                            size="sm"
                            className="marketingExpenseActionBtn"
                            onClick={() => {
                              closePaymentListModal();
                              deleteExpense(expense);
                            }}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="marketingExpensePaymentListEmpty">
                    No payments recorded for this card yet.
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closePaymentListModal}>
            Close
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => {
              const budgetId = activeBudgetCard?.budgetId || activeBudgetCard?.docID || activeBudgetCard?.id || '';
              closePaymentListModal();
              openCreateModal(budgetId);
            }}
            disabled={!activeBudgetCard}
          >
            <FontAwesomeIcon icon={faWallet} /> Make payment
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={modalShow} onHide={closeModal} centered contentClassName="marketingExpensesModal">
        <Modal.Header closeButton={!submitting}>
          <Modal.Title>{editing ? 'Edit payment' : 'Make payment'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Budget line</Form.Label>
              <Form.Select
                name="budgetId"
                value={formState.budgetId}
                onChange={handleFormChange}
                required
                disabled={budgets.length === 0}
              >
                <option value="">Select budget</option>
                {budgets.map((budget) => (
                  <option key={budget.docID || budget.id} value={budget.docID || budget.id}>
                    {budget.medium} - {formatKes(budget.amount)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Payment description</Form.Label>
              <Form.Control
                type="text"
                name="expenseTitle"
                value={formState.expenseTitle}
                onChange={handleFormChange}
                placeholder={selectedBudget?.medium ? `${selectedBudget.medium} payment` : 'Enter a short payment note'}
                required
              />
            </Form.Group>
            <div className="marketingExpenseModalGrid">
              <Form.Group className="mb-3">
                <Form.Label>Payment date</Form.Label>
                <Form.Control
                  type="date"
                  name="expenseDate"
                  value={formState.expenseDate}
                  onChange={handleFormChange}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Amount</Form.Label>
                <Form.Control
                  type="number"
                  name="expenseAmount"
                  value={formState.expenseAmount}
                  onChange={handleFormChange}
                  required
                />
              </Form.Group>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                name="paymentStatus"
                value={formState.paymentStatus}
                onChange={handleFormChange}
              >
                {EXPENSE_STATUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="notes"
                value={formState.notes}
                onChange={handleFormChange}
                placeholder="Optional note for this payment"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={resetForm} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'Saving...' : editing ? 'Update payment' : 'Save payment'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default MarketingExpensesComp;
